import React from 'react'
import { X, Info, Bot, MessageSquare, Filter, Tag } from 'lucide-react'
import { FilterOptions } from '../types'

interface ImageMetadataModalProps {
  isOpen: boolean
  onClose: () => void
  metadata: {
    prompt: string
    filters: FilterOptions
    model?: string
    footPersona?: {
      name: string
      prompt: string
    }
    generatedAt?: string
    imageUrl: string
  }
}

const ImageMetadataModal: React.FC<ImageMetadataModalProps> = ({
  isOpen,
  onClose,
  metadata
}) => {
  if (!isOpen) return null

  const getActiveFilters = () => {
    return Object.entries(metadata.filters)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => ({ key, value }))
  }

  const activeFilters = getActiveFilters()

  const formatFilterName = (key: string) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Image Metadata</h2>
              <p className="text-sm text-gray-500">Generation details and information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto space-y-6">
          {/* Image Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3">Generated Image</h3>
            <img
              src={metadata.imageUrl}
              alt="Generated image"
              className="w-full h-48 object-cover rounded-lg"
            />
          </div>

          {/* Generation Details */}
          <div className="space-y-4">
            {/* Model Used */}
            {metadata.model && (
              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Bot className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-green-900">AI Model</h3>
                </div>
                <p className="text-green-700 font-mono text-sm bg-green-100 px-3 py-2 rounded-lg">
                  {metadata.model}
                </p>
              </div>
            )}

            {/* Foot Persona */}
            {metadata.footPersona && (
              <div className="bg-pink-50 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <MessageSquare className="w-5 h-5 text-pink-600" />
                  <h3 className="font-medium text-pink-900">Foot Persona</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-pink-700 font-semibold">{metadata.footPersona.name}</p>
                  <p className="text-pink-600 text-sm bg-pink-100 px-3 py-2 rounded-lg italic">
                    "{metadata.footPersona.prompt}"
                  </p>
                </div>
              </div>
            )}

            {/* User Query */}
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-900">User Prompt</h3>
              </div>
              <p className="text-blue-700 bg-blue-100 px-3 py-2 rounded-lg">
                "{metadata.prompt}"
              </p>
            </div>

            {/* Filters & Tags */}
            {activeFilters.length > 0 && (
              <div className="bg-purple-50 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Filter className="w-5 h-5 text-purple-600" />
                  <h3 className="font-medium text-purple-900">Applied Filters</h3>
                  <span className="bg-purple-200 text-purple-700 text-xs px-2 py-1 rounded-full">
                    {activeFilters.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {activeFilters.map(({ key, value }, index) => (
                    <div key={index} className="bg-purple-100 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <Tag className="w-3 h-3 text-purple-600" />
                        <span className="text-xs font-medium text-purple-700 uppercase tracking-wide">
                          {formatFilterName(key)}
                        </span>
                      </div>
                      <p className="text-purple-800 text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Generation Timestamp */}
            {metadata.generatedAt && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-medium text-gray-900 mb-2">Generation Time</h3>
                <p className="text-gray-600 text-sm font-mono">
                  {new Date(metadata.generatedAt).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageMetadataModal 